// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
// Constants for the spreadsheet components.

import { JOB_REQUEST_FIELDS, GRID_TEAM_COLUMN_NAMES } from "./job-request";

export const SHEETS_PAGE_SIZE = 20000;
export const SHEETS_HEADINGS_HEIGHT = 377;  // TBD: Should be computed dynamically in case the layout is changed
export const ITWINS_TEST_HEADINGS_HEIGHT = 250;
export const SSCHEDULE_HEADINGS_HEIGHT = 300;

export const SSCHEDULE_DEFAULT_PAGE_SIZE = 10; // Number of sites per page in the schedule page.

export const VARIANCE_WARNING_THRESHOLD = 10; // % of overdue over plan period to turn on warning color.

// keys for grid group settings.
export const GROUPING_INFO = "groupInfo";
export const GROUPING_DETAILS = "groupDetails";
export const GROUPING_DELI = "groupDeli"; // May need one for each deliverable.
export const GROUPING_VISIT = "groupVisit";
export const GROUPING_DOWNLOAD = "groupDownload";

export const GROUPING_LM_GENINFO = "groupLMGenInfo";
export const GROUPING_LM_GEOMETRY = "groupLMGeometry";
export const GROUPING_LM_PANELS = "groupLMPanels";
export const GROUPING_LM_MEMBERS = "groupLMMembers";

export const GROUPING_WA_SUMMARY = "groupWASummary";
export const GROUPING_WA_EQUIPMENTS = "groupWAEquipments";
export const GROUPING_WA_CALCULATIONS = "groupWACalculations";

// keys for grid settings stored in the LocalStorage.
//export const STATUS_GRID_SETTINGS = "statusGridSettings";
export const SUMMARY_GRID_SETTINGS = "summaryGridSettings";
export const SITES_GRID_SETTINGS = "sitesGridSettings";
export const DETAILS_GRID_SETTINGS = "detailsGridSettings";
export const TEAM_GRID_SETTINGS = "teamGridSettings";
export const SHIPPING_GRID_SETTINGS = "shippingGridSettings";
export const CATALOG_GRID_SETTINGS = "catalogGridSettings";
export const TRACKING_GRID_SETTINGS = "trackingGridSettings";

// keys for grid column widths stored in the LocalStorage.
export const SUMMARY_GRID_WIDTHS = "summaryGridWidths";
//export const STATUS_GRID_WIDTHS = "statusGridWidths";
export const SITES_GRID_WIDTHS = "sitesGridWidths";
export const DETAILS_GRID_WIDTHS = "detailsGridWidths";
export const TEAM_GRID_WIDTHS = "teamGridWidths";
export const SHIPPING_GRID_WIDTHS = "shippingGridWidths";
export const CATALOG_GRID_WIDTHS = "catalogGridWidths";
export const TRACKING_GRID_WIDTHS = "trackingGridWidths";

// keys for grid collapse settings.
export const STATUS_GRID_COLLAPSED_ITEMS = "statusCollapsedItems";
export const SUMMARY_GRID_COLLAPSED_ITEMS = "summaryCollapsedItems";
export const SITES_GRID_COLLAPSED_ITEMS = "sitesCollapsedItems";
export const VISIT_GRID_COLLAPSED_ITEMS = "visitCollapsedItems";
export const DETAILS_GRID_COLLAPSED_ITEMS = "detailsCollapsedItems";
export const TEAM_GRID_COLLAPSED_ITEMS = "teamCollapsedItems";
export const SHIPPING_GRID_COLLAPSED_ITEMS = "shippingCollapsedItems";
export const CATALOG_GRID_COLLAPSED_ITEMS = "catalogCollapsedItems";
export const TRACKING_GRID_COLLAPSED_ITEMS = "trackingCollapsedItems";
export const DOWNLOAD_GRID_COLLAPSED_ITEMS = "downloadCollapsedItems";

// keys for grid scrolling positions.
export const STATUS_GRID_SCROLLING_POSITION = "statusScrollingPos";

export const LM_GENINFO_GRID_COLLAPSED_ITEMS = "lmGenInfoCollapsedItems";
export const LM_GEOMETRY_GRID_COLLAPSED_ITEMS = "lmGeometryCollapsedItems";
export const LM_PANELS_GRID_COLLAPSED_ITEMS = "lmPanelsCollapsedItems";
export const LM_MEMBERS_GRID_COLLAPSED_ITEMS = "lmMembersCollapsedItems";

export const WA_SUMMARY_GRID_COLLAPSED_ITEMS = "waSummaryCollapsedItems";
export const WA_EQUIPMENTS_GRID_COLLAPSED_ITEMS = "waEquipmentsCollapsedItems";
export const WA_CALCULATIONS_GRID_COLLAPSED_ITEMS = "waCalculationsCollapsedItems";

// Misc keys
export const CURRENT_TAB_INDEX = "currentTabIndex";
export const CURRENT_LM_TAB_INDEX = "currentLMTabIndex";
export const CURRENT_WA_TAB_INDEX = "currentWATabIndex";
export const LAST_V_SCROLL_POS = "lastVScroll";
export const CURRENT_WA_EQUIPMENT_VIEW = "currentWAEquipmentView";
export const CURRENT_STAFF_TAB_INDEX = "currentStaffTabIndex";

export const SELECTED_ORDER_ID = "selectedOrderId";
export const LM_SELECTED_ORDER_ID = "selectedOrderIdLM";
export const WA_SELECTED_ORDER_ID = "selectedOrderIdWA";

export const ORDER_FILTERS = "orderFilters";
export const LM_ORDER_FILTERS = "orderFiltersLM";
export const WA_ORDER_FILTERS = "orderFiltersWA";

export const MAP_SETTINGS = "mapSettings";

// Misc labels
export const COPY_SCHEDULE_DIALOG_TITLE = "Paste schedule";
export const CHANGE_ADDRESS_DIALOG_TITLE = "Change address";

// Next three hardcoded lists/dictionaries must match.  TBD: Maybe generated from the JobRequestDTO.
export const CONDITION_LABEL_ORDER = [
    JOB_REQUEST_FIELDS.emi,
    JOB_REQUEST_FIELDS.multipleTowers,
    JOB_REQUEST_FIELDS.highAltitude,
    JOB_REQUEST_FIELDS.underConstruction,
    JOB_REQUEST_FIELDS.roadAccessIssues,
    JOB_REQUEST_FIELDS.otherConditions,
];
export const CONDITION_LABEL_MAP = {
    emi: 'EMI',
    multipleTowers: 'Multiple towers',
    highAltitude: 'High altitude',
    underConstruction: 'Under construction',
    roadAccessIssues: 'Road access issues',
    otherConditions: 'Other conditions'
};
export const REVERSE_CONDITION_LABEL_MAP = {
    'EMI': 'emi',
    'Multiple towers': 'multipleTowers',
    'High altitude': 'highAltitude',
    'Under construction': 'underConstruction',
    'Road access issues': 'roadAccessIssues',
    'Other conditions': 'otherConditions'
};

export const GRID_SUMMARY_COLUMN_NAMES = {
    name: 'name',
    rank: 'rank',
    completionStatus: 'completionStatus',
    variance: 'variance',
    tag1: 'tag1',
    tag2: 'tag2',
    processingCost: 'processingCost'    
};

// Column names for the Workflow grid page.
export const GRID_STATUS_COLUMN_LABELS = {
    deliverableId: 'deliverableId',
    name: 'name',                                       // Site
    deliverableName: 'deliverableName',                 // Task
    dimensionLabel: 'dimensionLabel',                   // Stage
    vendorName: 'vendorName',                           // Vendor
    completionStatusStr: 'completionStatusStr',         // Status
    variance: 'variance',                               // Variance
    exceptionCode: 'exceptionCode',                     // Issue/Flag
    planStartDate: 'planStartDate',                     // Plan start
    dueDate: 'dueDate',                                 // Plan end/Due or completed. deadline in portal-api DB.
    forecastStartDate: 'forecastStartDate',             // forecast start
    forecastCompletionDate: 'forecastCompletionDate',   // forecast end
    startDate: 'startDate',                             // Actual start
    actualCompletionDate: 'actualCompletionDate',       // Actual end
    timeInMinutes: 'timeInMinutes',                     // Minutes
    lastComment: 'lastComment'                          // Comment
};

export const GRID_STATUS_DATE_COLUMN_LABELS = [
    GRID_STATUS_COLUMN_LABELS.planStartDate,
    GRID_STATUS_COLUMN_LABELS.dueDate,
    GRID_STATUS_COLUMN_LABELS.forecastStartDate,
    GRID_STATUS_COLUMN_LABELS.forecastCompletionDate,
    GRID_STATUS_COLUMN_LABELS.startDate,
    GRID_STATUS_COLUMN_LABELS.actualCompletionDate
];

// Use this table to configure the order of the column header/size and column visibility (?) of the grid in SDeliverableInfo (Workflow tab).
// visible not present: always visible
// visible is a dictionary: Visible if at least one user role is in dictionary.
// visible is an empty dictionary: Not visible.
export const GRID_STATUS_COLUMN_HEADING_MAP = {
    [GRID_STATUS_COLUMN_LABELS.deliverableId]: {'toolTip': 'Internal ID.', 'label': 'ID', 'visible': {}}, 
    [GRID_STATUS_COLUMN_LABELS.name]: {'toolTip': 'Public name of site.', 'label': 'Site'}, 
    [GRID_STATUS_COLUMN_LABELS.deliverableName]: {'toolTip': 'Task to work on.', 'label': 'Task'}, 
    [GRID_STATUS_COLUMN_LABELS.dimensionLabel]: {'toolTip': 'Group of related tasks.', 'label': 'Stage'}, 
    [GRID_STATUS_COLUMN_LABELS.vendorName]: {'toolTip': 'Responsible vendor.', 'label': 'Vendor'}, 
    [GRID_STATUS_COLUMN_LABELS.completionStatusStr]: {'toolTip': 'Task\'s status.', 'label': 'Status'}, 
    [GRID_STATUS_COLUMN_LABELS.variance]: {'toolTip': 'Difference in days between planned and actual completion date.', 'label': 'Variance'}, 
    [GRID_STATUS_COLUMN_LABELS.exceptionCode]: {'toolTip': 'warning, moderate or severe.', 'label': 'Flag'},
    [GRID_STATUS_COLUMN_LABELS.planStartDate]: {'toolTip': 'Plan start date.', 'label': 'Plan start'},
    [GRID_STATUS_COLUMN_LABELS.dueDate]: {'toolTip': 'Planned completion date.', 'label': 'Plan end'},
    [GRID_STATUS_COLUMN_LABELS.forecastStartDate]: {'toolTip': 'Forecast start date.', 'label': 'Forecast start'},
    [GRID_STATUS_COLUMN_LABELS.forecastCompletionDate]: {'toolTip': 'Forecast completion date.', 'label': 'Forecast end'},
    [GRID_STATUS_COLUMN_LABELS.startDate]: {'toolTip': 'Actual start date.', 'label': 'Actual start'},
    [GRID_STATUS_COLUMN_LABELS.actualCompletionDate]: {'toolTip': 'Actual completion date.', 'label': 'Actual end'},
    [GRID_STATUS_COLUMN_LABELS.timeInMinutes]: {'toolTip': 'Time spent on task.', 'label': 'Minutes'},
    [GRID_STATUS_COLUMN_LABELS.lastComment]: {'toolTip': 'Task\'s most recent comment. Click on icon to view all comments.', 'label': 'Comments'},
}

export const GRID_FOLDERS_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'], 
    'folder': {'toolTip': 'The folder path holding flight collection.', 'label': 'Collection folder'}, 
    'folderServer': {'toolTip': 'The computer holding flight collection.', 'label': 'Collection server'}, 
    'folderLiDAR': {'toolTip': 'The folder path holding LiDAR collection', 'label': 'LiDAR folder'}, 
    'serverLiDAR': {'toolTip': 'The computer holding LiDAR collection.', 'label': 'LiDAR server'}
}

export const GRID_INFO_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'], 
    'serialNumber': {'toolTip': 'Customer\'s site code.', 'label': 'Site ID'}, 
    'legacyIDCode': {'toolTip': 'Legacy site code.', 'label': 'Legacy ID'}, 
    'lat': {'toolTip': 'Latitude', 'label': 'Latitude'}, 
    'lng': {'toolTip': 'Longitude', 'label': 'Longitude'}, 
    'height': {'toolTip': 'Height', 'label': 'Height'},
    'buildingHeight': {'toolTip': 'Height', 'label': 'Building height'},
    'type': {'toolTip': 'Construction type (e.g., Monopole, Lattice, Guyed wire, etc.', 'label': 'Tower type'},
    'lastComment': {'toolTip': 'Site\'s most recent comment.', 'label': 'Last comment'},
    'address': {'toolTip': 'Street addres', 'label': 'Address'},
    'condition': {'toolTip': 'Issues such as high EMI, under construction, high altitude, etc.', 'label': 'Issues'}
}
export const GRID_DETAIL_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'], 
    'SiteType': {'toolTip': 'Site type', 'label': 'Site type'}, 
    'numberOfTenants': {'toolTip': 'Number of tenants', 'label': 'Tenants'}, 
    'complexityRating': {'toolTip': 'Complexity rating', 'label': 'Complexity rating'}, 
    'manufacture': {'toolTip': 'Manufacture of tower', 'label': 'Manufacture'}, 
    'model': {'toolTip': 'Tower model', 'label': 'Model'}, 
    'technology': {'toolTip': 'Supported wireless technologies', 'label': 'Technology'}, 
    'usefulLoadCapacity': {'toolTip': 'Useful load capacity', 'label': 'Load Capacity %'}, 
    'reinforceNReplacement': {'toolTip': 'Reinforce and Replacement type', 'label': 'ReInforce/Replacement'}, 
}

// Column names for the Status grid page.
export const GRID_VISIT_COLUMN_NAMES = {
    name: GRID_STATUS_COLUMN_HEADING_MAP['name'],
    visitName: 'visitName',
    lat: 'lat',
    lon: 'lon',
    height: 'height',
    region: 'region',
    dueDate: 'dueDate',
    typeName: 'typeName',
    siteTypeName: 'siteTypeName'
};

export const GRID_VISIT_COLUMN_HEADING_MAP = {
    'name': {'toolTip': 'Site name on record.', 'label': 'Site'},
    'visitName': {'toolTip': 'Actual site name on visit.', 'label': 'Visit name'},
    'lat': {'toolTip': 'Actual latitude on visit', 'label': 'Visit latitude'}, 
    'lon': {'toolTip': 'Actual longitude on visit', 'label': 'Visit longitude'}, 
    'height': {'toolTip': 'Actual height on visit', 'label': 'Visit height (m)'},
    'region': {'toolTip': 'Actual state or Region on visit', 'label': 'Visit region'},
    'typeName': {'toolTip': 'Actual construction type on visit (e.g., Monopole, Lattice, Guyed wire, etc.)', 'label': 'Visit tower type'},
    'siteTypeName': {'toolTip': 'Actual site type on visit', 'label': 'Visit site type'}
}

export const GRID_LIDAR_COLUMN_HEADING_MAP = {
}

export const GRID_SHIPPING_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'], 
    'collectionShipDate': { 'toolTip': 'Shipment date of flight collection.', 'label': 'Collection shipped'},
    'collectionMethodAndTracking': { 'toolTip': 'Shipment method (e.g., FedEx, UPS) and tracking number.', 'label': 'Collection tracking'},
    'collectionReceiveDate': { 'toolTip': 'Receiving date of flight collection.', 'label': 'Collection received'},
    'visualsShipDate': { 'toolTip': 'Shipment date of ground collection.', 'label': 'Ground shipped'},
    'visualsMethodAndTracking': { 'toolTip': 'Shipment method (e.g., FedEx, UPS) and tracking number.', 'label': 'Ground tracking'},
    'visualsReceiveDate': { 'toolTip': 'Receiving date of ground collection.', 'label': 'Ground received'},
    'LiDARShipDate': { 'toolTip': 'Shipment date of LiDAR collection.', 'label': 'LiDAR shipped'},
    'LiDARMethodAndTracking': { 'toolTip': 'Shipment method (e.g., FedEx, UPS) and tracking number.', 'label': 'LiDAR tracking'},
    'LiDARReceiveDate': { 'toolTip': 'Receiving date of LiDAR collection.', 'label': 'LiDAR received'},

}
export const GRID_TEAM_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'],
    [GRID_TEAM_COLUMN_NAMES.vendor]: { 'toolTip': 'Responsible vendor.', 'label': 'Vendor'},
    [GRID_TEAM_COLUMN_NAMES.pilot]: { 'toolTip': 'Drone pilot.', 'label': 'Pilot'},
    [GRID_TEAM_COLUMN_NAMES.teamName]: { 'toolTip': 'Team Number.', 'label': 'Team #'},
    [GRID_TEAM_COLUMN_NAMES.carMake]: { 'toolTip': 'Vehicle make.', 'label': 'Auto make'},
    [GRID_TEAM_COLUMN_NAMES.carModel]: { 'toolTip': 'Vehicle model.', 'label': 'Auto model'},
    [GRID_TEAM_COLUMN_NAMES.carRegistration]: { 'toolTip': 'Vehicle Registration.', 'label': 'Auto license'},
    [GRID_TEAM_COLUMN_NAMES.observer]: { 'toolTip': 'Visual observer.', 'label': 'Observer'},
    [GRID_TEAM_COLUMN_NAMES.postProcessingTechnician]: { 'toolTip': 'Post-processing technician.', 'label': 'Processing tech'},
    [GRID_TEAM_COLUMN_NAMES.productionTechnician]: { 'toolTip': 'Production tech.', 'label': 'Production tech'}
};

export const GRID_TRACKING_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'], 
    'priority': {'toolTip': '1: Very urgent, 2: Urgent, 3: In progress, 4: Pending, 5: Closed, 6: Canceled.', 'label': 'Status'},
    'actualFlight': {'toolTip': 'Actual flight date', 'label': 'Actual drone flight'}, 
    'dataAcceptedAtVI': {'toolTip': 'Data uploaded to VI and accepted', 'label': 'Uploaded to server'},
    '_3dModelCreated': {'toolTip': '3D model created.', 'label': '3D model'},
    'otiqAndAiProcessing': {'toolTip': 'Bentley OTIQ host set up and AI processed.', 'label': 'OTIQ & AI processed'},
    'measurementExtraction': {'toolTip': 'Structure measurement extraction.', 'label': 'Measurement extraction'},
    'drawingCompleted': {'toolTip': 'Structure drawings PDF uploaded.', 'label': 'Drawing completed'},
    'structureAnalysis': {'toolTip': 'Structural analysis report completed.', 'label': 'Structure analysis'},
    'forecastDelivery': {'toolTip': 'Forecast delivery date', 'label': 'Forecast delivery'},
    'reportSubmitted': {'toolTip': 'Final notification to customer.', 'label': 'Report submitted'},
    'siteAccepted': {'toolTip': 'Customer acceptance.', 'label': 'Site accepted'},
    'siteInvoiceSubmitted': {'toolTip': 'Final invoicing.', 'label': 'Site invoice submitted'},
};

export const GRID_DOWNLOAD_COLUMN_HEADING_MAP = {
    'name': GRID_STATUS_COLUMN_HEADING_MAP['name'],
    'acs': { 'toolTip': 'ACS files.', 'label': 'ACS file'},
    'pdf': { 'toolTip': 'Field QC PDF', 'label': 'PDF quality report'},
};

// Lattice Measurement General Info - Column Names
export const GRID_LM_GENERAL_INFO_COLUMN_NAMES = {
    towerId: 'towerId',
    name: GRID_STATUS_COLUMN_HEADING_MAP['name'],
    type: 'type',
    conLegs: 'conLegs',
    conBracings: 'conBracings',
    numOfLegs: 'numOfLegs',
    legType: 'legType',
    bracingType: 'bracingType',
    orientation: 'orientation',
    horizontalType: 'horizontalType',
    redundantType: 'redundantType',
    planBraceType: 'planBraceType',
    hipBraceType: 'hipBraceType'
}

// Lattice Measurement Geometry - Column Names
export const GRID_LM_GEOMETRY_COLUMN_NAMES = {
    towerId: 'towerId',
    name: GRID_STATUS_COLUMN_HEADING_MAP['name'],
    height: 'height',
    baseHeight: 'baseHeight',
    studHeight: 'studHeight',
    sectionHeight: 'sectionHeight',
    numberOfSections: 'numberOfSections',
    baseWidth: 'baseWidth',
    topWidth: 'topWidth',
    kink1Height: 'kink1Height',
    kink1Width: 'kink1Width',
    kink2Height: 'kink2Height',
    kink2Width: 'kink2Width',
    kink3Height: 'kink3Height',
    kink3Width: 'kink3Width',
    kink4Height: 'kink4Height',
    kink4Width: 'kink4Width',
    kink5Height: 'kink5Height',
    kink5Width: 'kink5Width'
}

// Lattice Measurement Panel - Column Names
export const GRID_LM_PANEL_COLUMN_NAMES = {
    id: 'id',
    name: GRID_STATUS_COLUMN_HEADING_MAP['name'],
    panelNumber: 'panelNumber',
    section: 'section',
    height: 'height',
    type: 'type',
    notes: 'notes'
}

// Lattice Measurement Member - Column Names
export const GRID_LM_MEMBER_COLUMN_NAMES = {
    id: 'id',
    name: GRID_STATUS_COLUMN_HEADING_MAP['name'],
    panelNumber: 'panelNumber',
    panelName: 'panelName',
    conType: 'conType',
    x: 'x',
    y: 'y',
    diameter: 'diameter',
    thickness: 'thickness',
    boltSize: 'boltSize',
    numberOfBolts: 'numberOfBolts'
}

// Wind Area\Equipments - Column Names
export const GRID_WA_EQUIPMENTS_COLUMN_NAMES = {
    id: 'id',
    site: GRID_STATUS_COLUMN_HEADING_MAP['name'],
    name: 'name',
    elevation: 'elevation',
    face: 'face',
    make: 'make',
    model: 'model',
    type: 'type',
    height: 'height',
    width: 'width',
    depth: 'depth',
    azimuth: 'azimuth',
    tilt: 'tilt',
    lateralOffset: 'lateralOffset',
    standoffDistance: 'standoffDistance',
    verticalOffset: 'verticalOffset'
}

// Lattice Measurement tabs
export const GRID_LM_GENERAL_INFO_COLUMN_HEADING_MAP = {
    'name': {'toolTip': 'Site name on record.', 'label': 'Site'},
    'type': {'toolTip': '.', 'label': 'Type'},
    'conLegs': {'toolTip': '.', 'label': 'Connections (Leg)'},
    'conBracings': {'toolTip': '.', 'label': 'Connections (Bracing)'},
    'numOfLegs': {'toolTip': '.', 'label': '# of legs'},
    'legType': {'toolTip': '.', 'label': 'Leg type'},
    'bracingType': {'toolTip': '.', 'label': 'Bracing type'},
    'orientation': {'toolTip': '.', 'label': 'Tower orientation'},
    'horizontalType': {'toolTip': '.', 'label': 'Horizontal type'},
    'redundantType': {'toolTip': '.', 'label': 'Redundant type'},
    'planBraceType': {'toolTip': '.', 'label': 'Plan brace type'},
    'hipBraceType': {'toolTip': '.', 'label': 'Hip brace type'}
}

export const GRID_LM_GEOMETRY_COLUMN_HEADING_MAP = {
    'name': {'toolTip': 'Site name on record.', 'label': 'Site'},
    'height': {'toolTip': '.', 'label': 'Height'},
    'baseHeight': {'toolTip': '.', 'label': 'Base height'},
    'studHeight': {'toolTip': '.', 'label': 'Stud height'},
    'sectionHeight': {'toolTip': '.', 'label': 'Section heights'},
    'numberOfSections': {'toolTip': '.', 'label': '# of sections'},
    'baseWidth': {'toolTip': '.', 'label': 'Base width'},
    'topWidth': {'toolTip': '.', 'label': 'Top width'},
    'kink1Height': {'toolTip': '.', 'label': 'Height to 1st kink'},
    'kink1Width': {'toolTip': '.', 'label': 'Width @ 1st kink'},
    'kink2Height': {'toolTip': '.', 'label': 'Height to 2nd kink', 'visible': {}},
    'kink2Width': {'toolTip': '.', 'label': 'Width @ 2nd kink', 'visible': {}},
    'kink3Height': {'toolTip': '.', 'label': 'Height to 3rd kink', 'visible': {}},
    'kink3Width': {'toolTip': '.', 'label': 'Width @ 3rd kink', 'visible': {}},
    'kink4Height': {'toolTip': '.', 'label': 'Height to 4th kink', 'visible': {}},
    'kink4Width': {'toolTip': '.', 'label': 'Width @ 4th kink', 'visible': {}},
    'kink5Height': {'toolTip': '.', 'label': 'Height to 5th kink', 'visible': {}},
    'kink5Width': {'toolTip': '.', 'label': 'Width @ 5th kink', 'visible': {}}
}

export const GRID_LM_PANELS_COLUMN_HEADING_MAP = {
    'id': {'toolTip': 'Panel ID.', 'label': 'ID', 'visible': {}},
    'name': {'toolTip': 'Site name.', 'label': 'Site'},
    'panelNumber': {'toolTip': 'Panel number.', 'label': 'Panel #'},
    'section': {'toolTip': 'Section number.', 'label': 'Section'},
    'height': {'toolTip': 'Height.', 'label': 'Height'},
    'type': {'toolTip': 'Panel type.', 'label': 'Type'},
    'notes': {'toolTip': 'Notes.', 'label': 'Notes'}
}

export const GRID_LM_MEMBERS_COLUMN_HEADING_MAP = {
    'id': {'toolTip': 'Member ID.', 'label': 'ID', 'visible': {}},
    'name': {'toolTip': 'Site name.', 'label': 'Site'},
    'panelNumber': {'toolTip': 'Panel number.', 'label': 'Panel #'},
    'memberName': {'toolTip': 'Member name.', 'label': 'Name'},
    'conType': {'toolTip': 'Connection type.', 'label': 'Type'},
    'x': {'toolTip': 'X.', 'label': 'X'},
    'y': {'toolTip': 'Y.', 'label': 'Y'},
    'diameter': {'toolTip': 'Diameter.', 'label': 'Diameter'},
    'thickness': {'toolTip': 'Thickness.', 'label': 'Thickness'},
    'boltSize': {'toolTip': 'Bolt size.', 'label': 'Bolt size'},
    'numberOfBolts': {'toolTip': 'Number of bolts.', 'label': '# of bolts'}
}

export const GRID_WA_SUMMARY_COLUMN_HEADING_MAP = {
    'id': {'toolTip': 'Equipment ID.', 'label': 'ID', 'visible': {}},   // 0
    'site': {'toolTip': 'Site name.', 'label': 'Site'},                 // 1
    'region': {'toolTip': 'Region.', 'label': 'Region'},                // 2
    'numberOfOperators': {'toolTip': 'Number of operators.', 'label': '# Operators'},  // 3
    'latitude': {'toolTip': 'Latitude.', 'label': 'Latitude'},                         // 4
    'longitude': {'toolTip': 'Longitude.', 'label': 'Longitude'},                      // 5
    'siteType': {'toolTip': 'Site type.', 'label': 'Site type'},                       // 6
    'structureType': {'toolTip': 'Structure type.', 'label': 'Structure type'},        // 7
    'buildingHeight': {'toolTip': 'Building height.', 'label': 'Building height'},     // 8
    'structureHeight': {'toolTip': 'Structure height.', 'label': 'Structure height'},  // 9
    'epaWindArea': {'toolTip': 'EPA wind area.', 'label': 'EPA wind Area'}             // 10
}

export const GRID_WA_EQUIPMENTS_COLUMN_HEADING_MAP = {
    'id': {'toolTip': 'Equipment ID.', 'label': 'ID', 'visible': {}},   // 0
    'site': {'toolTip': 'Site name.', 'label': 'Site'},                 // 1
    'name': {'toolTip': 'Equipment name.', 'label': 'Name'},            // 2
    'type': {'toolTip': 'Equipment type.', 'label': 'Type'},            // 3
    'status': {'toolTip': 'Status.', 'label': 'Status'},                // 4
    'operator': {'toolTip': 'Equipment type.', 'label': 'Operator'},    // 5
    'face': {'toolTip': 'Face/Leg.', 'label': 'Face'},                  // 6
    'make': {'toolTip': 'Make.', 'label': 'Make'},                      // 7
    'model': {'toolTip': 'Model.', 'label': 'Model'},                   // 8
    'elevation': {'toolTip': 'Elevation.', 'label': 'Elevation'},       // 9
    'height': {'toolTip': 'Height.', 'label': 'Height'},                // 10
    'width': {'toolTip': 'Width.', 'label': 'Width'},                   // 11
    'depth': {'toolTip': 'Depth.', 'label': 'Depth'},                   // 12
    'diameter': {'toolTip': 'Diameter.', 'label': 'Diameter'},          // 13
    'azimuth': {'toolTip': 'Azimuth.', 'label': 'Azimuth (deg)'},       // 14
    'tilt': {'toolTip': 'Tilt.', 'label': 'Tilt (deg)'},                // 15
    'lateralOffset': {'toolTip': 'Lateral Offset.', 'label': 'Lateral offset'},             // 16
    'standoffDistance': {'toolTip': 'Standoff Distance.', 'label': 'Standoff distance'},    // 17
    'verticalOffset': {'toolTip': 'Vertical Offset.', 'label': 'Vertical offset'},          // 18
    'area': {'toolTip': 'Area.', 'label': 'Area'},                                          // 19
    'aspectRatio': {'toolTip': 'Aspect ratio.', 'label': 'Aspect ratio'},                   // 20
    'coefficient': {'toolTip': 'Coefficient (Ca).', 'label': 'Coefficient'},                // 21
    'epaAntenna': {'toolTip': 'EPA Antenna.', 'label': 'EPA antenna'}                       // 22
}

export const GRID_WA_CALCULATIONS_COLUMN_HEADING_MAP = {
    'id': {'toolTip': 'Equipment ID.', 'label': 'ID', 'visible': {}},
    'site': {'toolTip': 'Site name.', 'label': 'Site'},
    'name': {'toolTip': 'Equipment name.', 'label': 'Name'},
    'type': {'toolTip': 'Equipment type.', 'label': 'Type'},
    'operator': {'toolTip': 'Equipment type.', 'label': 'Operator'},
    'make': {'toolTip': 'Make.', 'label': 'Make'},
    'model': {'toolTip': 'Model.', 'label': 'Model'},
    'status': {'toolTip': 'Status.', 'label': 'Status'},
    'elevation': {'toolTip': 'Elevation.', 'label': 'Elevation'},
    'azimuth': {'toolTip': 'Azimuth.', 'label': 'Azimuth (deg)'},
    'face': {'toolTip': 'Face/Leg.', 'label': 'Face'},
    'height': {'toolTip': 'Height.', 'label': 'Height'},
    'width': {'toolTip': 'Width.', 'label': 'Width'},
    'depth': {'toolTip': 'Depth.', 'label': 'Depth'},
    'diameter': {'toolTip': 'Diameter.', 'label': 'Diameter'},
    'area': {'toolTip': 'Area.', 'label': 'Area'},
    'aspectRatio': {'toolTip': 'Aspect ratio.', 'label': 'Aspect ratio'},
    'coefficient': {'toolTip': 'Coefficient (Ca).', 'label': 'Coefficient'},
    'epaAntenna': {'toolTip': 'EPA Antenna.', 'label': 'EPA antenna'}
}

// Proivders for the tower data
export const REPO_PROVIDERS = {
    'iTwins': 'iTwins',
    'portal-api': 'portal-api',
    'template': 'template'
}

